import Loader from "@/components/Loader";
import api from "@/http/api";
import type { Customer } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@univdiam/ui/components/button";
import FeaturedIcon from "@univdiam/ui/customComponents/Kanban/FeaturedIcon";
import { normalizeUrl } from "@univdiam/ui/lib/UrlNormalize";
import { format } from "date-fns";
import { ArrowLeft, LoaderCircle, User } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";


const getCustomerData = (id: string) => {
    return api.get(`/admin/customer/${id}`);
};
const verifyCustomer = (verify: boolean, customerId: string) => {

    const sendData = {
        verify
    }

    return api.patch(`/admin/customer/update/${customerId}`, sendData);

}
const CustomerDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { data, isPending } = useQuery({
        queryKey: ['customers', id],
        queryFn: async () => {
            return await getCustomerData(id!);
        },
    });

    const { mutate, isPending: isVerifyPending } = useMutation({
        mutationFn: (verify: boolean) =>
            verifyCustomer(verify, id!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["customers", id] });
        },
        onError: (error) => {
            toast.error("Something went wrong " + error.message);
        },
    });
    const customer = data?.data?.customer as Customer




    if (isPending && !customer) {
        return <Loader />
    }


    return (
        <div className="max-w-3xl mx-auto p-6 space-y-6">
            <div className='flex justify-between items-center px-4 py-3 bg-white border border-[#E9EAEB] rounded-[8px]'>
                <div className='flex items-center gap-2'>
                    <div className='border rounded-[4px] p-1 cursor-pointer hover:bg-gray-100 transition-colors' onClick={() => navigate(-1)}>
                        <ArrowLeft size={12} strokeWidth={1} />
                    </div>
                    <p className='text-body2 font-semibold text-gray-800'>{customer?.full_name}</p>
                </div>
                <div className='flex items-center gap-1'>
                    {
                        customer?.status === 1 ? <Button onClick={() => mutate(false)}>{
                            isVerifyPending ? <LoaderCircle className="animate-spin" /> : "Revoke Verification"
                        }</Button> : <Button onClick={() => mutate(true)}>{
                            isVerifyPending ? <LoaderCircle className="animate-spin" /> : "Verify"
                        }</Button>
                    }
                </div>
            </div>


            <div className='flex flex-col bg-white border border-[#E9EAEB] rounded-md'>
                <div className=' px-4 py-3 flex  justify-between items-center border-b border-[#E9EAEB]'>
                    <div className='  flex items-center align-center gap-2'>
                        <FeaturedIcon />
                        <div className='text-body2 mb-1'>Profile Details</div>
                    </div>
                </div>
                <div className="flex gap-3 items-center p-4">
                    <div className="w-[50px] h-[50px]">
                        {customer?.profile_pic ? (
                            <img
                                src={normalizeUrl(customer?.profile_pic)}
                                alt="Profile"
                                className="w-full h-full object-cover border rounded-full"
                            />
                        ) : (
                            <div className="w-full h-full border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center">
                                <User className="w-full h-full text-gray-400" />
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="text-xl font-semibold">{customer?.full_name}</p>
                        <p className="caption">{customer?.email}</p>
                    </div>
                </div>
                <div className='flex grow-1 p-4 gap-4 text-body2'>
                    <div className="w-[50%] flex flex-col gap-3">
                        <div className='grid grid-cols-[50%_50%]'>Phone number: <span className='font-semibold'>{customer?.phone_number}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Active since: <span className='font-semibold'>{customer?.created_at ? format(new Date(customer?.created_at), 'dd MMM yyyy') : '-'}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Verified: <span className='font-semibold'>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${customer?.status
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {customer?.status ? 'Yes' : 'No'}
                            </span></span></div>


                    </div>
                    <div className="w-[50%] flex flex-col gap-3">
                        <div className='grid grid-cols-[50%_50%]'>Email verified: <span className='font-semibold'>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${customer?.email_verified
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {customer?.email_verified ? 'Yes' : 'No'}
                            </span></span></div>
                        <div className='grid grid-cols-[50%_50%]'>Phone verified: <span className='font-semibold'>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${customer?.phone_verified
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {customer?.phone_verified ? 'Yes' : 'No'}
                            </span></span></div>
                    </div>
                </div>
            </div>
            <div className='flex flex-col bg-white border border-[#E9EAEB] rounded-md'>
                <div className=' px-4 py-3 flex  justify-between items-center border-b border-[#E9EAEB]'>
                    <div className='  flex items-center align-center gap-2'>
                        <FeaturedIcon />
                        <div className='text-body2 mb-1'>Business Details</div>
                    </div>
                </div>
                <div className="flex gap-3 items-center p-4">
                    <div className="w-[50px] h-[50px]">
                        {customer?.company_logo ? (
                            <img
                                src={normalizeUrl(customer?.company_logo)}
                                alt="Profile"
                                className="w-full h-full object-cover border rounded-full"
                            />
                        ) : (
                            <div className="w-full h-full border-2 border-dashed border-gray-300 flex items-center justify-center rounded-full">
                                <User className="w-full h-full text-gray-400 rounded-full" />
                            </div>
                        )}
                    </div>
                </div>
                <div className='flex grow-1 p-4 gap-4 text-body2'>
                    <div className="w-[50%] flex flex-col gap-3">
                        <div className='grid grid-cols-[50%_50%]'>Business name: <span className='font-semibold'>{customer?.business_name}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Phone number: <span className='font-semibold'>{customer?.business_phone_number}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Website: <span className='font-semibold'>{customer?.website}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Address line 1: <span className='font-semibold'>{customer?.address_line_1}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Address line 2: <span className='font-semibold'>{customer?.address_line_2}</span></div>
                        <div className='grid grid-cols-[50%_50%]'>Location: <span className='font-semibold' title={[customer?.city, customer?.state, customer?.country, customer?.zip_code].filter(Boolean).join(', ')}>{[customer?.city, customer?.state, customer?.country, customer?.zip_code].filter(Boolean).join(', ')}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default CustomerDetail